import { useEffect, useState, useMemo } from "react";
import { Box, Typography, Chip, Input, Button, DialogTitle } from "@mui/joy";
import { Dialog, DialogActions, DialogContent, Paper } from "@mui/material";
import { Close, FileDownload, PictureAsPdf } from "@mui/icons-material";
import { connect } from "react-redux";
import DataTable from "react-data-table-component";
import { GET_TRANSACTION_HISTORY_API_URL } from "../../configs/url";
import apiCall, { flatten } from "../../helpers/api";
import {
  datatable_style,
  downloadTablePdf,
  exportToCsv,
  LoadingComponent,
  NoDataComponent,
} from "../../helpers/datatable_helpers";
import CustomDateRangePicker from "../../helpers/CustomDateRangePicker";
import { useTranslation } from "react-i18next";

const Transactions = (props) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
    key: "selection",
  });
  const [filteredRows, setFilteredRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { settings } = props;
  const { t } = useTranslation();

  useEffect(() => {
    if (!props?.user || !props?.user?.auth_token) {
      window.location.href = "/login";
    }
    document.title = t("sidebar_statement") + " | KPG Partner";
  }, []);

  const dataColumns = [
    {
      data: "type",
      name: "type",
      searchable: true,
      title: t("statement_column_type"),
    },
    {
      data: "amount",
      name: "amount",
      searchable: true,
      title: t("statement_column_amount"),
    },
    {
      data: "description",
      name: "description",
      searchable: true,
      title: t("statement_column_description"),
    },
    {
      data: "created_at",
      name: "created_at",
      searchable: true,
      title: t("statement_column_date"),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    const data = {
      draw: 1,
      start: perPage * (page - 1),
      length: perPage,
      search: { value: search, regex: false },
      columns: dataColumns,
    };
    try {
      const res = await apiCall({
        method: "POST",
        url:
          GET_TRANSACTION_HISTORY_API_URL +
          "?" +
          new URLSearchParams(flatten(data)),
        data: { start_date: dates.startDate, end_date: dates.endDate },
      });
      const processedData = res.data.data.map((row) => ({
        type: row.type || "",
        amount: Number(row.amount) || 0,
        description: row.description || "NA",
        created_at: row.created_at || "NA",
      }));
      setRows(processedData);
      setFilteredRows(processedData);
      setTotal(res.data.recordsFiltered);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearch(searchValue);
    setPage(1);
  };

  useEffect(() => {
    fetchData();
  }, [props?.user, search, page, perPage]);

  const columns = useMemo(
    () => [
      {
        name: t("statement_column_type"),
        selector: (row) => row.type,
        sortable: true,
        minWidth: "120px",
        maxWidth: "150px",
        cell: (row) => (
          <Chip
            variant="soft"
            color={
              row.type === "deposit"
                ? "success"
                : row.type === "withdraw"
                ? "danger"
                : "neutral"
            }
            size="sm"
          >
            {row.type.toUpperCase()}
          </Chip>
        ),
      },
      {
        name: t("statement_column_amount"),
        selector: (row) => Math.abs(row.amount),
        sortable: true,
        minWidth: "120px",
        maxWidth: "160px",
        cell: (row) => (
          <Typography component="span" className="amount-text">
            {settings[5].value} {Math.abs(row.amount)}
          </Typography>
        ),
      },
      {
        name: t("statement_column_description"),
        selector: (row) => row.description,
        sortable: true,
        grow: 1,
        cell: (row) => (
          <Typography
            level="body-sm"
            className="description-text"
            title={row.description}
          >
            {row.description}
          </Typography>
        ),
      },
      {
        name: t("statement_column_date"),
        selector: (row) => row.created_at,
        sortable: true,
        minWidth: "160px",
        maxWidth: "200px",
        cell: (row) => (
          <Typography level="body-sm">{row.created_at}</Typography>
        ),
      },
    ],
    []
  );

  return (
    <Box className="transactions-container">
      <Box className="header-container">
        <Typography level="h4">{t("sidebar_statement")}</Typography>
        <Box className="controls-container">
          <CustomDateRangePicker
            dates={dates}
            setDates={setDates}
            onApply={fetchData}
          />
          <Input
            size="sm"
            placeholder={t("common_search")}
            value={search}
            onChange={handleSearchChange}
            className="search-input"
          />
          <Box className="export-buttons-container">
            <Button
              size="sm"
              variant="soft"
              onClick={() => exportToCsv(filteredRows, "transactions")}
              disabled={total === 0}
              startDecorator={<FileDownload sx={{ fontSize: "16px" }} />}
              className="export-btn csv-btn"
            >
              {t("common_csv")}
            </Button>
            <Button
              size="sm"
              variant="soft"
              onClick={() =>
                downloadTablePdf(dataColumns, filteredRows, "transactions")
              }
              disabled={total === 0}
              startDecorator={<PictureAsPdf sx={{ fontSize: "16px" }} />}
              className="export-btn pdf-btn"
            >
              {t("common_pdf")}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box className="table-wrapper">
        <Paper elevation={0} className="table-paper">
          <DataTable
            columns={columns}
            data={filteredRows}
            paginationServer
            paginationTotalRows={total}
            progressPending={loading}
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            pagination
            paginationPerPage={perPage}
            onChangeRowsPerPage={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            responsive
            striped
            highlightOnHover
            customStyles={datatable_style}
            paginationComponentOptions={{
              rowsPerPageText: t("common_rows_per_page"),
              rangeSeparatorText: t("common_dt_separator"),
              selectAllRowsItem: false,
              selectAllRowsItemText: t("common_all"),
            }}
            onChangePage={(page) => {
              setPage(page);
            }}
            dense={window.innerWidth < 768}
          />
        </Paper>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  settings: state.settings.settings,
});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Transactions);
